'use client';

import { Box, Typography } from '@mui/material';
import HomeLandingShell from './HomeLandingShell';
import HomeHeroBackground from './HomeHeroBackground';
import HomeHeroNav from './HomeHeroNav';
import HomeLandingFooter from './HomeLandingFooter';
import { HOME_BRAND, HOME_NAV_BAR_PX } from './home-landing-data';

/** Matches HomeHeroNav logo box height on md+; logo square mark is ~1:1 with that height. */
const HERO_LOGO_SQUARE_SIZE_MD = 52;

/** Legibility over bright / busy hero photography */
const heroTextLegibility = {
  color: 'common.white',
  textShadow: [
    '-0.5px -0.5px 0 rgba(0, 0, 0, 0.75)',
    '0.5px -0.5px 0 rgba(0, 0, 0, 0.75)',
    '-0.5px 0.5px 0 rgba(0, 0, 0, 0.75)',
    '0.5px 0.5px 0 rgba(0, 0, 0, 0.75)',
    '0 0 2px rgba(0, 0, 0, 0.6)',
    '0 2px 10px rgba(0, 0, 0, 0.45)',
  ].join(', '),
};

export default function HomeLandingPage() {
  return (
    <HomeLandingShell>
      <Box
        component="main"
        sx={{
          position: 'relative',
          flex: 1,
          height: '100%',
          overflow: 'hidden',
          color: 'common.white',
        }}
      >
        <HomeHeroBackground />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
          }}
        >
          <Box
            component="img"
            src="/imgs/svgs/TRVertical.svg"
            alt=""
            aria-hidden
            sx={{
              position: 'absolute',
              left: theme =>
                `calc(${theme.spacing(HOME_NAV_BAR_PX.md)} + ${HERO_LOGO_SQUARE_SIZE_MD / 2}px)`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: { xs: 'none', md: 'block' },
              height: '70%',
              width: 'auto',
              pointerEvents: 'none',
            }}
          />

          <HomeHeroNav logoHref="#top" />

          <Box
            id="top"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                component="p"
                sx={{
                  ...heroTextLegibility,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  fontWeight: 400,
                  fontSize: { xs: 'clamp(1.65rem, 6.4vw, 2.75rem)', md: '3.75rem' },
                  lineHeight: 1.15,
                  mb: 1,
                }}
              >
                {HOME_BRAND.taglineLine1}
                <br />
                <Box component="span" sx={{ fontWeight: 600, letterSpacing: '0.06em' }}>
                  {HOME_BRAND.taglineLine2}
                </Box>
              </Typography>

              <Typography
                component="h1"
                sx={{
                  ...heroTextLegibility,
                  textTransform: 'uppercase',
                  fontWeight: 400,
                  fontSize: { xs: 'clamp(1.35rem, 4vw, 2rem)', md: '2.5rem' },
                  mt: 2,
                  lineHeight: 1.3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    letterSpacing: '0.35em',
                    marginRight: '-0.35em',
                  }}
                >
                  {HOME_BRAND.headlineLine1}
                </Box>
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    fontSize: '0.42em',
                    lineHeight: 1.3,
                    letterSpacing: 'normal',
                    px: '0.9em',
                  }}
                >
                  ●
                </Box>
                <Box component="span" sx={{ letterSpacing: '0.28em' }}>
                  {HOME_BRAND.headlineLine2}
                </Box>
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 2,
            }}
          >
            <HomeLandingFooter />
          </Box>
        </Box>
      </Box>
    </HomeLandingShell>
  );
}
